import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useBillingReconciliationDpeActions, useSendBillingReconciliationDpeActions } from '../../hooks/api/useBillingReconciliationHooks.js';
import { DPE_ACTION_FIELDS, dpeActionLabel } from './dpeActionModel.js';

const FIELD_LABELS = {
  referenceId: 'Reference ID',
  dealerPrice: 'Dealer price',
  filler: 'Filler',
  gmBrandCd: 'GM brand',
  vendorCd: 'Vendor',
  effectiveDate: 'Effective',
  selectionDate: 'Selection',
  billingDate: 'Billing',
  lastUpdatedDate: 'Last updated',
  productCd: 'Product code',
  productDescription: 'Product description',
  designation: 'Designation',
  status: 'Status',
};
const ROUND_ALL = 'all';
const BASE_COLUMN_SX = {
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
};
const PAYLOAD_COLUMN_WIDTHS = {
  referenceId: 190,
  dealerPrice: 110,
  filler: 90,
  gmBrandCd: 110,
  vendorCd: 90,
  effectiveDate: 120,
  selectionDate: 120,
  billingDate: 120,
  lastUpdatedDate: 135,
  productCd: 150,
  productDescription: 260,
  designation: 130,
  status: 125,
};
const columnSx = (minWidth) => ({
  ...BASE_COLUMN_SX,
  minWidth,
});
const payloadColumnSx = (field) => columnSx(PAYLOAD_COLUMN_WIDTHS[field] || 120);
const statusColor = (status) => {
  if (status === 'sent') return 'success';
  if (status === 'error') return 'error';
  if (status === 'saved') return 'info';
  if (status === 'inherited_pending') return 'secondary';
  return 'warning';
};
const sameIds = (left = [], right = []) => left.length === right.length && left.every((id, index) => id === right[index]);
const signatureForAction = (row = {}) => JSON.stringify({
  id: row.id,
  queueStatus: row.queueStatus,
  updatedAt: row.updatedAt || row.updated_at || null,
  payload: row.vmtPayload || null,
});
const dryRunResultIsClean = (result = {}, expectedCount = 0) => (
  !result.validationErrors?.length
  && !result.skippedActionIds?.length
  && Number(result.count ?? expectedCount) === expectedCount
  && Number(result.validCount ?? expectedCount) === expectedCount
);
const listRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};
const isSendableRow = (row, activeRunId = null) => (
  ['draft', 'saved', 'error'].includes(row.queueStatus)
  && (!activeRunId || row.runId === activeRunId)
);
const dpeActionDescriptor = (row = {}) => {
  const productCode = row.vmtPayload?.productCd || row.productCd || row.productCode || 'product';
  return `${row.id || 'unknown action'} for BAC ${row.bac || 'unknown'} ${productCode}`;
};
const rowSelectionLabel = (row = {}) => `Select DPE action ${dpeActionDescriptor(row)}`;
const rowActionLabel = (row = {}) => `DPE action row for ${dpeActionDescriptor(row)}`;
const isInteractiveTarget = (target) => Boolean(target?.closest?.('button,input,select,textarea,a,[role="button"],[role="checkbox"]'));

const DpeActionsPanel = ({
  runId,
  bac = null,
  limit = 500,
  program = null,
  serviceMonth = null,
  iterations = [],
}) => {
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunValidatedSignature, setDryRunValidatedSignature] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [roundValue, setRoundValue] = useState(ROUND_ALL);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const roundOptions = useMemo(() => (iterations || [])
    .map((iteration) => ({
      value: String(iteration.roundNumber || iteration.round || ''),
      label: iteration.roundNumber ? `Round ${iteration.roundNumber}` : iteration.label || iteration.id,
    }))
    .filter((option) => option.value), [iterations]);
  const query = useBillingReconciliationDpeActions(
    {
      runId,
      program,
      serviceMonth,
      bac,
      limit,
      roundMode: roundValue === ROUND_ALL ? 'all' : 'current',
      roundNumber: roundValue === ROUND_ALL ? null : roundValue,
      includeSuperseded: includeSuperseded ? 'true' : null,
    },
    { enabled: Boolean(runId || (program && serviceMonth)), refetchOnMount: 'always' },
  );
  const sendMutation = useSendBillingReconciliationDpeActions();
  const rows = useMemo(() => listRows(query.data), [query.data]);
  const sendableRows = useMemo(() => rows.filter((row) => isSendableRow(row, runId)), [rows, runId]);
  const sendableIds = useMemo(() => sendableRows.map((row) => row.id), [sendableRows]);
  const selectedSendableIds = useMemo(() => (
    selectedIds.filter((id) => sendableRows.some((row) => row.id === id))
  ), [selectedIds, sendableRows]);
  const selectedSendableSignature = useMemo(() => selectedSendableIds
    .map((id) => sendableRows.find((row) => row.id === id))
    .filter(Boolean)
    .map(signatureForAction)
    .join('|'), [selectedSendableIds, sendableRows]);
  const selectedIdsHaveCleanDryRun = selectedSendableIds.length > 0
    && selectedSendableSignature === dryRunValidatedSignature;
  useEffect(() => {
    setSelectedIds((current) => {
      const kept = current.filter((id) => sendableIds.includes(id));
      const next = current.length ? kept : [];
      return sameIds(current, next) ? current : next;
    });
  }, [sendableIds]);
  const toggleAll = () => setSelectedIds(() => (
    selectedSendableIds.length === sendableIds.length ? [] : sendableIds
  ));
  const toggleRow = (id) => setSelectedIds((current) => (
    current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]
  ));
  const runSend = async (dryRun) => {
    const result = await sendMutation.mutateAsync({ runId, bac, ids: selectedSendableIds, dryRun });
    setDryRunResult(result);
    if (dryRun) {
      setDryRunValidatedSignature(dryRunResultIsClean(result, selectedSendableIds.length) ? selectedSendableSignature : '');
      return;
    }
    setDryRunValidatedSignature('');
    setConfirmSendOpen(false);
  };

  return (
    <Stack spacing={1.5}>
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <BoxTitle title="DPE Actions" subtitle="Queued VMT payloads staged from billing reconciliation." />
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="dpe-actions-round-label">Round</InputLabel>
              <Select
                labelId="dpe-actions-round-label"
                value={roundValue}
                label="Round"
                onChange={(event) => {
                  setSelectedIds([]);
                  setDryRunResult(null);
                  setDryRunValidatedSignature('');
                  setRoundValue(event.target.value);
                }}
              >
                <MenuItem value={ROUND_ALL}>All rounds</MenuItem>
                {roundOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant={includeSuperseded ? 'contained' : 'outlined'}
              onClick={() => {
                setSelectedIds([]);
                setIncludeSuperseded((current) => !current);
              }}
            >
              {includeSuperseded ? 'Hide superseded' : 'Show superseded'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ScienceOutlinedIcon />}
              disabled={!selectedSendableIds.length || sendMutation.isPending}
              onClick={() => runSend(true)}
            >
              Dry run
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<SendOutlinedIcon />}
              disabled={!selectedSendableIds.length || !selectedIdsHaveCleanDryRun || sendMutation.isPending}
              onClick={() => setConfirmSendOpen(true)}
            >
              Send to DPE
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {dryRunResult && (
        <Alert severity={dryRunResult.validationErrors?.length ? 'warning' : 'success'}>
          {dryRunResult.dryRun ? 'Dry run' : 'Send'} checked {dryRunResult.count || 0} action(s)
          {dryRunResult.batches ? ` across ${dryRunResult.batches.length} VMT batch(es).` : '.'}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 2380 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={columnSx(48)}>
                <Checkbox
                  size="small"
                  checked={sendableRows.length > 0 && selectedSendableIds.length === sendableRows.length}
                  indeterminate={selectedSendableIds.length > 0 && selectedSendableIds.length < sendableRows.length}
                  disabled={!sendableRows.length}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'Select all sendable DPE actions' }}
                />
              </TableCell>
              <TableCell sx={columnSx(90)}>BAC</TableCell>
              <TableCell sx={columnSx(110)}>Round</TableCell>
              <TableCell sx={columnSx(210)}>Dealer</TableCell>
              <TableCell sx={columnSx(175)}>Action</TableCell>
              <TableCell sx={columnSx(120)}>Status</TableCell>
              {DPE_ACTION_FIELDS.map((field) => (
                <TableCell key={field} sx={payloadColumnSx(field)}>{FIELD_LABELS[field] || field}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && query.isFetching ? (
              <TableRow>
                <TableCell colSpan={DPE_ACTION_FIELDS.length + 6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Loading queued DPE actions...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={DPE_ACTION_FIELDS.length + 6}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No DPE actions have been queued for this run.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const selected = selectedIds.includes(row.id);
              const sendable = isSendableRow(row, runId);
              const toggleSelectedRow = () => {
                if (sendable) toggleRow(row.id);
              };
              const handleRowKeyDown = (event) => {
                if (!sendable || isInteractiveTarget(event.target) || (event.key !== 'Enter' && event.key !== ' ')) return;
                event.preventDefault();
                toggleSelectedRow();
              };
              return (
              <TableRow
                key={row.id}
                hover
                selected={selected}
                aria-selected={selected}
                tabIndex={sendable ? 0 : undefined}
                aria-label={sendable ? rowActionLabel(row) : undefined}
                onClick={(event) => {
                  if (isInteractiveTarget(event.target)) return;
                  toggleSelectedRow();
                }}
                onKeyDown={handleRowKeyDown}
                sx={{
                  cursor: sendable ? 'pointer' : 'default',
                  '&.Mui-selected': {
                    outline: '1px solid',
                    outlineColor: 'primary.main',
                  },
                }}
              >
                <TableCell
                  padding="checkbox"
                  sx={columnSx(56)}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    checked={selected}
                    disabled={!sendable}
                    onChange={() => toggleRow(row.id)}
                    inputProps={{ 'aria-label': rowSelectionLabel(row) }}
                    sx={{ p: 1.25 }}
                  />
                </TableCell>
                <TableCell sx={columnSx(90)}>{row.bac}</TableCell>
                <TableCell sx={columnSx(110)}>{row.roundLabel || (row.roundNumber ? `Round ${row.roundNumber}` : '--')}</TableCell>
                <TableCell sx={columnSx(210)}>{row.dealerName || '--'}</TableCell>
                <TableCell sx={columnSx(175)}>{dpeActionLabel(row.actionType)}</TableCell>
                <TableCell sx={columnSx(120)}>
                  <Chip size="small" color={statusColor(row.queueStatus)} variant="outlined" label={row.queueStatus || 'draft'} />
                </TableCell>
                {DPE_ACTION_FIELDS.map((field) => (
                  <TableCell key={field} sx={payloadColumnSx(field)}>{row.vmtPayload?.[field] ?? row[field] ?? '--'}</TableCell>
                ))}
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={confirmSendOpen}
        onClose={() => setConfirmSendOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Send selected DPE actions?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This will push {selectedSendableIds.length} selected action(s) to DPE/VMT. A clean dry run is required for the exact selected action set before sending.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={!selectedSendableIds.length || !selectedIdsHaveCleanDryRun || sendMutation.isPending}
            onClick={() => runSend(false)}
          >
            Send selected
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const BoxTitle = ({ title, subtitle }) => (
  <Stack spacing={0.25}>
    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{title}</Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
  </Stack>
);

export default DpeActionsPanel;
